'use es6';
/**
 * SHAPE
 *
  {
    isFetchingById: {
      123: true,
      124: false,
    },
    fetchErrorById: {
      123: new Error(...),
      124: null,
    },
    byId: {
      123: GlobalWidget,
      124: GlobalWidget,
    },
    summaries: {
      order: [123, 124],
      widgets: {
        123: GlobalWidgetSummary,
        124: GlobalWidgetSummary,
      },
    },
  }
*/

import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import { isEqual, omit, sortBy, without } from 'underscore';
import undoable, { includeAction, combineFilters } from 'redux-undo';
import { UNINITIALIZED, PENDING, FAILED, SUCCEEDED } from 'DesignData/enums/RequestStatus';
import GlobalWidget from 'DesignData/models/GlobalWidget';
import GlobalWidgetSummary from 'DesignData/models/widgets/GlobalWidgetSummary';
import { HISTORY_LIMIT } from 'DesignData/constants/UndoRedo';
import { GLOBAL_WIDGET_DELETE, GLOBAL_WIDGET_EDIT_BODY, GLOBAL_WIDGET_EDIT_SMART_RULES, GLOBAL_WIDGET_REMOVE_SMART_RULES, GLOBAL_WIDGET_FETCH, GLOBAL_WIDGET_PUBLISH, GLOBAL_WIDGET_RENAME, GLOBAL_WIDGET_REVERT, GLOBAL_WIDGETS_LIST_SUMMARIES_FETCH, GLOBAL_WIDGET_EDIT_BODY_UNDO, GLOBAL_WIDGET_EDIT_BODY_REDO, GLOBAL_WIDGET_UPGRADE, GLOBAL_WIDGET_CREATE_FROM_FILE, LAYOUT_WIDGET_MAKE_GLOBAL, LAYOUT_WIDGET_RESTORE_PARENT } from 'DesignData/actions/ActionTypes';

const getLabel = widget => widget.label;

const getId = widget => widget.id;

function getSortedOrder(widgets) {
  return sortBy(widgets, getLabel).map(getId);
}
/**
 * isFetchingById
 *
 * Signifies the pending status of fetches by global widget id.
 * True if an XHR request to GET /widgets is pending, false otherwise.
 */


function isFetchingById(state = SeamlessImmutable({}), action) {
  switch (action.type) {
    case GLOBAL_WIDGET_FETCH:
      {
        const {
          globalWidgetId,
          globalWidget,
          error
        } = action;
        const isFetching = !(globalWidget || error);
        return state.set(globalWidgetId, isFetching);
      }

    case GLOBAL_WIDGET_DELETE:
      {
        const {
          globalWidgetId,
          deleted
        } = action;

        if (!deleted) {
          return state;
        }

        return state.without(globalWidgetId);
      }

    default:
      {
        return state;
      }
  }
}
/**
 * fetchErrorById
 *
 * Last XHR fetch error per global widget by id.
 * State cannot be immutable since instances of Error cannot be immuted.
 */


function fetchErrorById(state = {}, action) {
  switch (action.type) {
    case GLOBAL_WIDGET_FETCH:
      {
        const {
          globalWidgetId,
          error
        } = action;

        if (error === undefined && state[globalWidgetId] === undefined) {
          return Object.assign({}, state, {
            [globalWidgetId]: null
          });
        }

        if (state[globalWidgetId] === error) {
          return state;
        }

        return Object.assign({}, state, {
          [globalWidgetId]: error || null
        });
      }

    case GLOBAL_WIDGET_DELETE:
      {
        const {
          globalWidgetId,
          deleted
        } = action;

        if (!deleted) {
          return state;
        }

        return omit(state, globalWidgetId);
      }

    default:
      {
        return state;
      }
  }
}
/**
 * byId
 */


function globalWidgetReducer(state = null, action) {
  switch (action.type) {
    case GLOBAL_WIDGET_FETCH:
      {
        const {
          globalWidget
        } = action;

        if (!globalWidget) {
          return state;
        }

        return new GlobalWidget(globalWidget);
      }

    case GLOBAL_WIDGET_PUBLISH:
    case GLOBAL_WIDGET_RENAME:
    case GLOBAL_WIDGET_REVERT:
    case GLOBAL_WIDGET_EDIT_SMART_RULES:
    case GLOBAL_WIDGET_REMOVE_SMART_RULES:
      {
        const {
          globalWidget
        } = action;

        if (!globalWidget) {
          return state;
        }

        return new GlobalWidget(globalWidget).set('hasEdits', false);
      }

    case GLOBAL_WIDGET_EDIT_BODY:
      {
        const {
          body: newWidgetBody
        } = action;
        const {
          body
        } = state;

        if (isEqual(body, newWidgetBody)) {
          return state;
        }

        return state.merge({
          body: newWidgetBody,
          hasEdits: true
        });
      }

    case GLOBAL_WIDGET_UPGRADE:
      {
        const {
          customWidget
        } = action;

        if (!customWidget) {
          return state;
        }

        return state.set('generatedCustomWidgetId', customWidget.id);
      }

    default:
      {
        return state;
      }
  }
}

function getGlobalWidgetReducer(globalWidgetId) {
  const onlyListenToSelf = filterAction => {
    return filterAction.globalWidgetId === globalWidgetId;
  };

  return undoable(globalWidgetReducer, {
    limit: HISTORY_LIMIT,
    filter: combineFilters(includeAction([GLOBAL_WIDGET_EDIT_BODY]), onlyListenToSelf),
    undoType: GLOBAL_WIDGET_EDIT_BODY_UNDO,
    redoType: GLOBAL_WIDGET_EDIT_BODY_REDO
  });
}

function byId(state = SeamlessImmutable({}), action) {
  const {
    globalWidgetId
  } = action;

  switch (action.type) {
    case GLOBAL_WIDGET_FETCH:
    case GLOBAL_WIDGET_PUBLISH:
    case GLOBAL_WIDGET_RENAME:
    case GLOBAL_WIDGET_REVERT:
    case GLOBAL_WIDGET_EDIT_SMART_RULES:
    case GLOBAL_WIDGET_REMOVE_SMART_RULES:
    case GLOBAL_WIDGET_EDIT_BODY:
    case GLOBAL_WIDGET_EDIT_BODY_UNDO:
    case GLOBAL_WIDGET_EDIT_BODY_REDO:
    case GLOBAL_WIDGET_UPGRADE:
      {
        const globalWidget = getGlobalWidgetReducer(globalWidgetId);
        return state.set(globalWidgetId, globalWidget(state[globalWidgetId], action));
      }

    case GLOBAL_WIDGET_DELETE:
      {
        const {
          deleted
        } = action;

        if (!deleted) {
          return state;
        }

        return state.without(globalWidgetId);
      }

    default:
      {
        return state;
      }
  }
}

function addSummary(state, globalWidget) {
  const newWidget = new GlobalWidgetSummary(globalWidget);
  const widgets = state.widgets.set(newWidget.id, newWidget);
  const widgetsByName = state.widgetsByName.set(newWidget.name, newWidget);
  const order = getSortedOrder(widgets);
  return state.merge({
    order,
    widgets,
    widgetsByName
  });
}
/*
 * summariesById
 */


function summaries(state = SeamlessImmutable({
  order: [],
  widgets: {},
  widgetsByName: {},
  fetchStatus: UNINITIALIZED,
  isInitialized: false
}), action) {
  switch (action.type) {
    case GLOBAL_WIDGETS_LIST_SUMMARIES_FETCH:
      {
        if (!(action.widgets || action.error)) {
          return state.set('fetchStatus', PENDING);
        }

        if (action.error) {
          return state.set('fetchStatus', FAILED);
        }

        const order = getSortedOrder(action.widgets);
        const widgets = {};
        const widgetsByName = {};
        action.widgets.forEach(widget => {
          const newWidget = new GlobalWidgetSummary(widget);
          widgets[widget.id] = newWidget;
          widgetsByName[widget.name] = newWidget;
        });
        return state.merge({
          order,
          widgets,
          widgetsByName,
          fetchStatus: SUCCEEDED,
          isInitialized: true
        });
      }

    case GLOBAL_WIDGET_PUBLISH:
    case GLOBAL_WIDGET_RENAME:
    case GLOBAL_WIDGET_REVERT:
    case GLOBAL_WIDGET_CREATE_FROM_FILE:
    case GLOBAL_WIDGET_EDIT_SMART_RULES:
    case GLOBAL_WIDGET_REMOVE_SMART_RULES:
    case LAYOUT_WIDGET_MAKE_GLOBAL:
      {
        const {
          globalWidget
        } = action;

        if (!globalWidget) {
          return state;
        }

        return addSummary(state, globalWidget);
      }

    case GLOBAL_WIDGET_DELETE:
      {
        const {
          globalWidgetId: _globalWidgetId,
          deleted
        } = action;

        if (!deleted) {
          return state;
        }

        const globalWidgetsToDelete = Array.isArray(_globalWidgetId) ? _globalWidgetId : [_globalWidgetId];
        return globalWidgetsToDelete.reduce((updatedState, globalWidgetId) => {
          if (!updatedState.widgets[globalWidgetId]) {
            return updatedState;
          }

          const globalWidgetName = updatedState.widgets[globalWidgetId].name;
          return updatedState.merge({
            order: without(updatedState.order, globalWidgetId),
            widgets: updatedState.widgets.without(globalWidgetId),
            widgetsByName: updatedState.widgetsByName.without(globalWidgetName)
          });
        }, state);
      }

    case LAYOUT_WIDGET_RESTORE_PARENT:
      {
        const {
          widgetType,
          parent: globalWidget
        } = action;

        if (!(widgetType === 'global_widget' && globalWidget)) {
          return state;
        }

        return addSummary(state, globalWidget);
      }

    default:
      return state;
  }
}
/*
 * Export
 */


export default combineReducers({
  isFetchingById,
  fetchErrorById,
  byId,
  summaries
});